import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Users, Briefcase, Layers, X, MousePointerClick } from 'lucide-react';
import { Button } from '../../components/ui/button';

export const DrilldownModal = ({ data, onClose }) => {
  const [activeTab, setActiveTab] = useState('departments');
  const navigate = useNavigate();

  if (!data) return null;

  const tabs = [
    { id: 'departments', label: 'Departments', icon: Building2, count: data.departments?.length },
    { id: 'supervisors', label: 'Supervisors', icon: Users, count: data.supervisors?.length },
    { id: 'employees', label: 'Top Employees', icon: Briefcase, count: data.employees?.length },
    { id: 'pay_types', label: 'Pay Types', icon: Layers, count: data.pay_types?.length }
  ];

  const summary = data.summary || {};

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="drilldown-modal-overlay"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="drilldown-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2E5C8A] text-white p-5 flex-shrink-0">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <MousePointerClick className="h-5 w-5" />
                Period Drill-Down
              </h2>
              <p className="text-white/80 mt-1 text-sm">
                {data.period?.start?.split(' ')[0]} to {data.period?.end?.split(' ')[0]}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="drilldown-close-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Summary row */}
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div className="bg-white/10 rounded-lg p-2.5 text-center">
              <p className="text-lg font-bold">{(summary.total_hours || 0).toLocaleString()}</p>
              <p className="text-xs text-white/70">Total Hours</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2.5 text-center">
              <p className="text-lg font-bold text-orange-300">{(summary.premium_hours || 0).toLocaleString()}</p>
              <p className="text-xs text-white/70">Premium Hours</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2.5 text-center">
              <p className="text-lg font-bold">{summary.premium_percent || 0}%</p>
              <p className="text-xs text-white/70">Premium %</p>
            </div>
            <div className="bg-white/10 rounded-lg p-2.5 text-center">
              <p className="text-lg font-bold">{summary.employee_count || 0}</p>
              <p className="text-xs text-white/70">Employees</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 flex flex-shrink-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-[#2E5C8A] text-[#2E5C8A]'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`drilldown-tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className="bg-slate-100 text-slate-600 text-xs px-1.5 py-0.5 rounded-full">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-5 overflow-y-auto flex-1">
          {activeTab === 'departments' && (
            <div className="space-y-3" data-testid="drilldown-departments">
              {data.departments?.length > 0 ? data.departments.map((dept, i) => (
                <div key={`dept-${i}`} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-[#2E5C8A]/10 flex items-center justify-center text-sm font-bold text-[#2E5C8A]">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{dept.name || 'Unknown'}</p>
                    <p className="text-xs text-slate-500">{dept.employee_count} employees</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-semibold text-[#FF8C42]">{dept.premium_hours?.toLocaleString()} premium hrs</p>
                    <div className="flex items-center gap-2 justify-end">
                      <div className="w-20 bg-slate-200 rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full bg-[#FF8C42]"
                          style={{ width: `${Math.min(dept.premium_percent || 0, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-600 w-10 text-right">{dept.premium_percent}%</span>
                    </div>
                  </div>
                </div>
              )) : (
                <p className="text-slate-500 text-center py-8">No department data available</p>
              )}
            </div>
          )}

          {activeTab === 'supervisors' && (
            <div className="space-y-3" data-testid="drilldown-supervisors">
              {data.supervisors?.length > 0 ? data.supervisors.map((sup, i) => (
                <div key={`sup-${i}`} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center text-sm font-bold text-purple-700">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{sup.name || 'Unknown'}</p>
                    <p className="text-xs text-slate-500">{sup.department} &middot; {sup.employee_count} employees</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-semibold text-[#FF8C42]">{sup.premium_hours?.toLocaleString()} premium hrs</p>
                    <div className="flex items-center gap-2 justify-end">
                      <div className="w-20 bg-slate-200 rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full bg-purple-500"
                          style={{ width: `${Math.min(sup.premium_percent || 0, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-600 w-10 text-right">{sup.premium_percent}%</span>
                    </div>
                  </div>
                </div>
              )) : (
                <p className="text-slate-500 text-center py-8">No supervisor data available</p>
              )}
            </div>
          )}

          {activeTab === 'employees' && (
            <div className="space-y-3" data-testid="drilldown-employees">
              {data.employees?.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-[#2E5C8A] bg-[#2E5C8A]/5 px-3 py-2 rounded-lg">
                  <MousePointerClick className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>Click any employee name to see their full profile</span>
                </div>
              )}
              {data.employees?.length > 0 ? data.employees.map((emp, i) => (
                <div key={emp.employee_id || `emp-${i}`} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center text-sm font-bold text-red-700">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    {emp.employee_id ? (
                      <button
                        onClick={() => { onClose(); navigate(`/employee/${emp.employee_id}`); }}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline truncate transition-colors cursor-pointer"
                        data-testid={`drilldown-emp-link-${emp.employee_id}`}
                      >
                        {emp.name || 'Unknown'}
                      </button>
                    ) : (
                      <p className="font-medium text-slate-800 truncate">{emp.name || 'Unknown'}</p>
                    )}
                    <p className="text-xs text-slate-500">{emp.department} &middot; {emp.job_title || 'N/A'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-semibold text-[#FF8C42]">{emp.premium_hours?.toLocaleString()} premium hrs</p>
                    <p className="text-xs text-slate-500">{emp.total_hours?.toLocaleString()} total &middot; {emp.premium_percent}% premium</p>
                  </div>
                </div>
              )) : (
                <p className="text-slate-500 text-center py-8">No employee data available</p>
              )}
            </div>
          )}

          {activeTab === 'pay_types' && (
            <div className="space-y-3" data-testid="drilldown-paytypes">
              {data.pay_types?.length > 0 ? data.pay_types.map((pt, i) => (
                <div key={`pt-${i}`} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${pt.is_premium ? 'bg-[#FF8C42]' : 'bg-[#4CAF93]'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800">{pt.pay_type}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${pt.is_premium ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                      {pt.is_premium ? 'Premium' : 'Non-Premium'}
                    </span>
                  </div>
                  <p className="font-semibold text-slate-800 flex-shrink-0">{pt.hours?.toLocaleString()} hrs</p>
                </div>
              )) : (
                <p className="text-slate-500 text-center py-8">No pay type data available</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 flex justify-end flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </motion.div>
    </motion.div>
  );
};
